import {
  Badge,
  Button,
  ButtonSelect,
  Icon,
  InputField,
} from "@saviynt/design-system";
import { FormattedMessage, useIntl } from "react-intl";
import { useListFieldFilters } from "./ListFieldFiltersContext";
import { FilterFieldPills } from "./FilterFieldPills";
import type { ListFilterFieldDefinition } from "./types";
import "./ListFieldFilters.css";
import "./ListFieldFilters.css";
export type ListFieldFilterToolbarProps = {
  searchValue: string;
  onSearchChange: (value: string) => void;
  isFilterOpen: boolean;
  onFilterOpenChange: (open: boolean) => void;
  definitions: ListFilterFieldDefinition[] | undefined;
  isLoading: boolean;
  isError: boolean;
  /** Defaults provided; override for app-specific copy or `react-intl` ids. */
  labels?: {
    searchPlaceholder?: string;
    filterButton?: string;
  };
  dataTestIds?: {
    searchInput?: string;
    filterButton?: string;
    clearAll?: string;
  };
  searchInputName?: string;
  className?: string;
};

/**
 * Search field + filter toggle; when open, a panel with `FilterFieldPills` and “Clear all”.
 * Use under `ListFieldFiltersProvider`.
 */
export function ListFieldFilterToolbar({
  searchValue,
  onSearchChange,
  isFilterOpen,
  onFilterOpenChange,
  definitions,
  isLoading,
  isError,
  labels = {},
  dataTestIds = {},
  searchInputName = "listSearch",
  className = "",
}: ListFieldFilterToolbarProps) {
  const intl = useIntl();
  const { activeFieldCount, clearAll } = useListFieldFilters();

  const searchPlaceholder =
    labels.searchPlaceholder ??
    intl.formatMessage({
      id: "21Oq0C",
      defaultMessage: "Search…",
      description: "Placeholder for list search next to filters",
    });

  const filterButtonLabel =
    labels.filterButton ??
    intl.formatMessage({
      id: "HafXo5",
      defaultMessage: "Filter",
      description: "Opens the filter field panel",
    });

  return (
    <div className={`flex flex-col gap-3 w-full mb-4 ${className}`.trim()}>
      <div className="flex items-center gap-4 w-full max-sm:flex-col max-sm:items-stretch">
        <div className="flex items-center gap-4">
          <InputField
            className="flex-1 min-w-[36rem] min-h-[3rem] rounded-[var(--border-radius-xs,0.25rem)] border border-[var(--color-border-secondary-subtlest,#cfd9e9)] bg-[var(--color-background-neutral-subtlest,#fff)] [font:var(--typography-body-2)] placeholder:text-[var(--color-foreground-neutral-subtle,#717385)] focus:outline-none"
            name={searchInputName}
            placeholder={searchPlaceholder}
            kind="search"
            prefixIcon={
              <Icon kind="Search" color="Neutral-600" size="smallMedium" />
            }
            value={searchValue}
            setValue={onSearchChange}
            size="medium"
            dataTestId={dataTestIds.searchInput ?? "list-field-filters-search"}
          />
          <ButtonSelect
            className="min-h-[3rem]"
            label={filterButtonLabel}
            onClick={() => onFilterOpenChange(!isFilterOpen)}
            isOpen={isFilterOpen}
            size="medium"
            BadgeComp={
              activeFieldCount > 0 ? (
                <Badge kind="dot" color="primary" size="small" />
              ) : undefined
            }
            prefixIcon={<Icon kind="Filter" color="Neutral-600" />}
            dataTestId={dataTestIds.filterButton ?? "list-field-filters-button"}
          />
        </div>
      </div>

      {isFilterOpen ? (
        <div className="flex flex-1 min-w-0 gap-4">
          <FilterFieldPills
            definitions={definitions}
            isLoading={isLoading}
            isError={isError}
          />
          <Button
            kind="ghost"
            size="small"
            className="shrink-0 self-center"
            onClick={clearAll}
            isDisabled={activeFieldCount === 0}
            dataTestId={dataTestIds.clearAll ?? "list-field-filters-clear-all"}
          >
            <FormattedMessage
              id="8rPApR"
              defaultMessage="Clear all"
              description="Clears every field filter in the list toolbar"
            />
          </Button>
        </div>
      ) : null}
    </div>
  );
}
